import React, { useState, useRef, useEffect, DragEvent } from 'react';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Textarea, LoadingButton, DialogShell, DialogHeader, DialogFooter, dialogButtonClass } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { uploadFile, formatFileSize } from '../services/fileApi';
import FileTypeIcon from './FileTypeIcon';

interface FileUploadDialogProps {
  isOpen: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  projectId: string;
  requirementId?: string;
  taskId?: string;
  milestoneId?: string;
}

const FileUploadDialog: React.FC<FileUploadDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  requirementId,
  taskId,
  milestoneId,
}) => {
  const { t } = useTranslation(['translation', 'pages']);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [description, setDescription] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 清理預覽 URL（當組件卸載時）
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (!isOpen) return null;

  const handleFileSelect = (file: File) => {
    // 檢查檔案大小（50MB 限制）
    if (file.size > 50 * 1024 * 1024) {
      toast.error(t('pages:pm.files.fileTooLarge'));
      return;
    }

    setSelectedFile(file);

    // 如果是圖片，生成預覽 URL
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error(t('pages:pm.files.pleaseSelectFile'));
      return;
    }

    setLoading(true);

    try {
      await uploadFile({
        file: selectedFile,
        projectId,
        description,
        requirementId,
        taskId,
        milestoneId,
      });

      toast.success(t('pages:pm.files.uploadSuccess'));
      onClose(true); // 通知父組件刷新列表
      resetForm();
    } catch (error: any) {
      console.error('上傳失敗:', error);
      toast.error(error.response?.data?.error || t('pages:pm.files.uploadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setDescription('');

    // 清理預覽 URL 防止記憶體洩漏
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <DialogShell onClose={handleClose} closeDisabled={loading} className="max-w-lg">
      <DialogHeader title={t('pages:pm.files.uploadFile')} onClose={handleClose} closeDisabled={loading} closeLabel={t('common.actions.close')} />

      <div className="p-6">
        {/* 拖拽上傳區域 */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging
              ? 'border-brand-500 bg-accent-soft'
              : 'border-slate-300 dark:border-slate-600 hover:border-brand-400 dark:hover:border-brand-500'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {selectedFile ? (
            <div className="space-y-3">
              {/* 圖片預覽或圖示 */}
              {previewUrl ? (
                <div className="relative mx-auto w-32 h-32 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700">
                  <img
                    src={previewUrl}
                    alt={selectedFile.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <FileTypeIcon mimeType={selectedFile.type} className="h-8 w-8 mx-auto" />
              )}

              {/* 檔案資訊 */}
              <div>
                <p className="text-sm font-medium text-fg break-all">{selectedFile.name}</p>
                <p className="text-[13px] text-fg-secondary tabular-nums">{formatFileSize(selectedFile.size)}</p>
              </div>

              {/* 更換檔案按鈕 */}
              <button
                type="button"
                onClick={() => {
                  if (previewUrl) {
                    URL.revokeObjectURL(previewUrl);
                    setPreviewUrl(null);
                  }
                  setSelectedFile(null);
                }}
                className="text-sm font-medium text-accent hover:underline"
              >
                {t('pages:pm.files.changeFile')}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <CloudArrowUpIcon className="h-8 w-8 mx-auto text-fg-secondary" />
              <p className="text-sm text-fg-secondary">{t('pages:pm.files.dragDropHint')}</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm font-medium text-accent hover:underline"
              >
                {t('pages:pm.files.clickToSelect')}
              </button>
              <p className="text-[13px] text-fg-muted">{t('pages:pm.files.maxFileSize')}</p>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileInputChange}
        />

        {/* 檔案描述 */}
        <div className="mt-4">
          <label htmlFor="fileDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('pages:pm.files.description')} ({t('common.form.optional')})
          </label>
          <Textarea
            id="fileDescription"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('pages:pm.files.descriptionPlaceholder')}
            rows={3}
          />
        </div>
      </div>

      <DialogFooter>
        <button type="button" onClick={handleClose} disabled={loading} className={dialogButtonClass.cancel}>
          {t('common.actions.cancel')}
        </button>
        <LoadingButton type="button" onClick={handleUpload} loading={loading} disabled={!selectedFile}>
          {loading ? t('common.status.uploading') : t('common.actions.upload')}
        </LoadingButton>
      </DialogFooter>
    </DialogShell>
  );
};

export default FileUploadDialog;
